import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, Instagram } from "lucide-react";
import { CONTACT, SERVICES } from "@/data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About", to: "/about" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  const solid = scrolled || pathname !== "/";
  const textCol = solid ? "text-charcoal" : "text-white";

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-white/95 backdrop-blur-md border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className={`leading-none ${textCol}`}>
          <div className="font-display text-2xl md:text-3xl tracking-[0.15em] font-extrabold">CEMTEX</div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          <NavLink to="/" data-testid="nav-link-home" className={({ isActive }) => `text-xs uppercase tracking-[0.18em] transition-colors ${textCol} hover:text-sand ${isActive ? "text-sand" : ""}`}>Home</NavLink>

          <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <NavLink to="/services" data-testid="nav-link-services" className={({ isActive }) => `flex items-center gap-1 text-xs uppercase tracking-[0.18em] transition-colors ${textCol} hover:text-sand ${isActive ? "text-sand" : ""}`}>
              Services <ChevronDown className="h-3 w-3" strokeWidth={2} />
            </NavLink>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="bg-white border border-black/5 shadow-xl min-w-[240px] py-2">
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="block px-6 py-3 text-sm text-charcoal hover:bg-offwhite hover:text-sand transition-colors">
                      {s.title}
                    </Link>
                  ))}
                  <Link to="/services/remediation" data-testid="nav-service-remediation" className="block px-6 py-3 text-sm text-charcoal hover:bg-offwhite hover:text-sand transition-colors">Remediation</Link>
                  <Link to="/services" className="block px-6 py-3 text-xs uppercase tracking-[0.18em] text-taupe border-t border-black/5 mt-1 hover:text-sand">All Services</Link>
                </div>
              </div>
            )}
          </div>

          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase()}`} className={({ isActive }) => `text-xs uppercase tracking-[0.18em] transition-colors ${textCol} hover:text-sand ${isActive ? "text-sand" : ""}`}>{n.label}</NavLink>
          ))}
        </nav>

        {/* Right */}
        <div className="hidden lg:flex items-center gap-6">
          <a href={CONTACT.phoneHref} data-testid="header-phone" className={`flex items-center gap-2 text-sm tracking-wide ${textCol} hover:text-sand transition-colors`}>
            <Phone className="h-4 w-4" strokeWidth={1.5} /> {CONTACT.phone}
          </a>
          <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="header-instagram" aria-label="Instagram" className={`${textCol} hover:text-sand transition-colors`}>
            <Instagram className="h-5 w-5" strokeWidth={1.5} />
          </a>
          <Link to="/contact" data-testid="header-quote-cta" className={`btn-sweep px-6 py-3 text-xs uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${solid ? "bg-charcoal text-white hover:text-charcoal" : "bg-sand text-charcoal"}`}>
            Get a Free Quote
          </Link>
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-4">
          <a href={CONTACT.phoneHref} data-testid="header-phone-mobile" className={`flex items-center gap-1.5 text-sm ${textCol}`}>
            <Phone className="h-4 w-4" strokeWidth={1.5} /> <span className="hidden sm:inline">{CONTACT.phone}</span>
          </a>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className={textCol} aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-white border-t border-black/5" data-testid="mobile-menu">
          <nav className="container-x py-6 flex flex-col gap-1">
            <Link to="/" className="py-3 text-sm uppercase tracking-[0.18em] text-charcoal">Home</Link>
            <div className="py-3 text-xs uppercase tracking-[0.18em] text-taupe">Services</div>
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="py-2 pl-4 text-sm text-charcoal">{s.title}</Link>
            ))}
            <Link to="/services/remediation" className="py-2 pl-4 text-sm text-charcoal">Remediation</Link>
            <Link to="/services" className="py-2 pl-4 text-sm text-sand">All Services</Link>
            {NAV.slice(1).map((n) => (
              <Link key={n.to} to={n.to} className="py-3 text-sm uppercase tracking-[0.18em] text-charcoal">{n.label}</Link>
            ))}
            <Link to="/contact" className="mt-4 bg-charcoal text-white text-center py-4 text-xs uppercase tracking-[0.2em]">Get a Free Quote</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
